import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => { setPost(data); setState("ready"); })
      .catch(() => setState("error"));
  }, [slug]);

  if (state === "loading") {
    return <div className="pt-40 mx-auto max-w-[1600px] px-6 sm:px-10" data-testid="blog-post-loading"><p style={{ color: "var(--muted-t)" }}>Loading…</p></div>;
  }
  if (state === "error" || !post) {
    return (
      <div className="pt-40 mx-auto max-w-[1600px] px-6 sm:px-10" data-testid="blog-post-notfound">
        <p className="mb-6" style={{ color: "var(--muted-t)" }}>Post not found.</p>
        <button onClick={() => navigate("/blog")} className="text-sm uppercase tracking-widest" style={{ color: "var(--accent)" }}>Back to Journal</button>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image || undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Person", name: post.author || "Yuva Kumar", url: "https://yuvadesign.in/" },
    publisher: { "@type": "Person", name: "Yuva Kumar" },
    keywords: (post.tags || []).join(", "),
    mainEntityOfPage: `https://yuvadesign.in/blog/${post.slug}`,
  };

  return (
    <div data-testid="blog-post-page">
      <Seo path={`/blog/${post.slug}`} type="article" description={post.excerpt || post.title} image={post.cover_image} keywords={(post.tags || []).join(", ")} jsonLd={jsonLd} />

      <article className="pt-36">
        <div className="mx-auto max-w-[860px] px-6 sm:px-10">
          <Reveal>
            <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm mb-10 hover:opacity-70 transition-opacity" style={{ color: "var(--muted-t)" }}>
              <ArrowLeft size={16} /> Journal
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {post.tags?.map((t) => <span key={t} className="eyebrow">{t}</span>)}
              <span className="text-sm" style={{ color: "var(--muted-t)" }}>{post.read_minutes} min read</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="display text-4xl sm:text-6xl leading-tight" style={{ color: "var(--text)" }}>{post.title}</h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm" style={{ color: "var(--muted-t)" }}>By {post.author || "Yuva Kumar"}</p>
          </Reveal>
        </div>

        {post.cover_image ? (
          <div className="mx-auto max-w-[1100px] px-6 sm:px-10 my-12">
            <Reveal>
              <div className="overflow-hidden" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
                <div className="aspect-[21/9] overflow-hidden">
                  <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" style={{ filter: "grayscale(0.2) contrast(1.05)" }} />
                </div>
              </div>
            </Reveal>
          </div>
        ) : <div className="h-8" />}

        <div className="mx-auto max-w-[720px] px-6 sm:px-10 pb-16">
          {post.content.split(/\n\n+/).map((para, i) => (
            <Reveal key={i} delay={0.03 * i}>
              <p className="text-lg leading-relaxed mb-6 whitespace-pre-wrap" style={{ color: "var(--muted-t)" }}>{para}</p>
            </Reveal>
          ))}
        </div>
      </article>

      <section className="py-20">
        <div className="mx-auto max-w-[720px] px-6 sm:px-10">
          <Link to="/contact" data-testid="blog-contact-cta" className="group flex items-center justify-between gap-6 p-8 surface-card" style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)" }}>
            <span className="display text-2xl sm:text-3xl" style={{ color: "var(--text)" }}>Enjoyed this? Let&apos;s talk.</span>
            <ArrowUpRight size={22} style={{ color: "var(--accent)" }} className="shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
